import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SelectTagInput from '@/components/SelectTagInput';
import { contextsService } from '@/services/contexts';
import { Language } from '@/types/languages';
import { areasService } from '@/services/areas';
import { Area } from '@/types/areas';

import { ContextFormData, SubmitPhaseProps } from '@/pages/settings/content/contexts/create/shared/types';

export default function Two({ onSubmit, onBack, isSubmitting }: SubmitPhaseProps) {
  const { register, watch, setValue, formState: { errors } } = useFormContext<ContextFormData>();
  
  const [languages, setLanguages] = useState<Language[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);

  const watchedData = watch();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [languagesData, areasData] = await Promise.all([
          contextsService.getLanguages(),
          areasService.getAll()
        ]);
        
        setLanguages(languagesData.data || []);
        setAreas(areasData.data || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Novo Contexto</h2>
        <p className="text-gray-600">Adicione um novo Contexto Ativável ao Culture Monitor.</p>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Informações Adicionais</h3>
          <p className="text-sm text-gray-600 mb-6">Insira abaixo informações adicionais sobre o Contexto.</p>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Região
              </label>
              <Input
                {...register('region')}
                placeholder="Região"
                className={`w-full ${errors.region ? 'border-red-500' : ''}`}
              />
              {errors.region && (
                <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>
              )}
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Idioma Predominante
              </label>
              <Select
                value={watchedData.predominantLanguage}
                onValueChange={(value) => setValue('predominantLanguage', value)}
              >
                <SelectTrigger className={`w-full ${errors.predominantLanguage ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Selecione o idioma" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.id} value={language.id}>
                      {language.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.predominantLanguage && (
                <p className="text-red-500 text-xs mt-1">{errors.predominantLanguage.message}</p>
              )}
            </div>
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hashtags Usuais
            </label>
            <SelectTagInput
              value={watchedData.usualHashtags || []}
              onChange={(tags) => {
                setValue('usualHashtags', tags, { shouldValidate: true });
              }}
              placeholder="Digite e Adicione"
              items={[]}
              loading={false}
            />
            {errors.usualHashtags && (
              <p className="text-red-500 text-xs mt-1">{errors.usualHashtags.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Principal
            </label>
            <Input
              {...register('mainWebsite')}
              placeholder="https://exemplo.com"
              className={`w-full ${errors.mainWebsite ? 'border-red-500' : ''}`}
            />
            {errors.mainWebsite && (
              <p className="text-red-500 text-xs mt-1">{errors.mainWebsite.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descritivo
            </label>
            <Textarea
              {...register('description')}
              placeholder="Descreva o Contexto."
              className={`w-full min-h-[100px] ${errors.description ? 'border-red-500' : ''}`}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Setor de Interesse
            </label>
            <Select
              value={watchedData.interestSectors}
              onValueChange={(value) => setValue('interestSectors', value, { shouldValidate: true })}
            >
              <SelectTrigger className={`w-full ${errors.interestSectors ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Selecione o setor" />
              </SelectTrigger>
              <SelectContent>
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.interestSectors && (
              <p className="text-red-500 text-xs mt-1">{errors.interestSectors.message}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        )}
        
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="bg-gray-800 text-white hover:bg-gray-900 flex items-center gap-2 px-8 py-3 ml-auto"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {isSubmitting ? 'Cadastrando...' : 'Cadastrar Contexto'}
        </Button>
      </div>
    </div>
  );
}
